<script setup>
import { ref } from 'vue'
import { RouterLink } from 'vue-router'
import { onClickOutside } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import LanguageSwitcher from '../LanguageSwitcher.vue'

const props = defineProps({
  isLoggedIn: {
    type: Boolean,
    required: true
  },
  isAdmin: {
    type: Boolean,
    default: false
  },
  loginRedirect: {
    type: [String, Object],
    default: '/login'
  },
  accentColors: {
    type: Object,
    required: true
  },
  accentColor: {
    type: String,
    required: true
  },
  isDark: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['toggleTheme', 'setAccentColor'])

const { t } = useI18n()

const showAccentDropdown = ref(false)
const accentDropdownRef = ref(null)

onClickOutside(accentDropdownRef, () => {
  showAccentDropdown.value = false
})

const handleAccentColorChange = (colorKey) => {
  emit('setAccentColor', colorKey, props.isDark)
  showAccentDropdown.value = false
}
</script>

<template>
  <div class="hidden lg:flex items-center space-x-2 lg:space-x-4 flex-shrink-0 ml-2">
    <!-- Language Switcher -->
    <LanguageSwitcher />

    <!-- Theme Toggle -->
    <button
      @click="$emit('toggleTheme')"
      class="p-1 rounded-full text-brand-muted hover:text-brand-accent transition-colors"
      :title="isDark ? t('theme.switch_light') : t('theme.switch_dark')"
    >
      <!-- Sun -->
      <svg v-if="isDark" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
      <!-- Moon -->
      <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>
    </button>

    <!-- Accent Color Selector -->
    <div class="relative" ref="accentDropdownRef">
      <button
        @click="showAccentDropdown = !showAccentDropdown"
        class="p-2 rounded-lg text-brand-muted hover:text-brand-accent hover:bg-brand-muted/10 transition-colors"
        :title="t('theme.accent_color')"
        :aria-label="t('theme.accent_color')"
        :aria-expanded="showAccentDropdown"
      >
        <div
          class="w-5 h-5 rounded-full border border-brand-muted/30"
          :style="{ backgroundColor: `rgb(${isDark ? accentColors[accentColor]?.dark.primary : accentColors[accentColor]?.light.primary})` }"
        ></div>
      </button>
      <Transition
        enter-active-class="transition ease-out duration-150"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="showAccentDropdown"
          class="absolute right-0 top-full mt-2 p-3 bg-brand-surface rounded-[20px] shadow-xl border border-brand-muted/10 z-50 min-w-[160px]"
        >
          <p class="text-xs font-bold text-brand-muted uppercase tracking-widest mb-2 px-1">{{ t('theme.accent_color') }}</p>
          <div class="flex flex-wrap gap-2 justify-center">
            <button
              v-for="(color, key) in accentColors"
              :key="key"
              @click="handleAccentColorChange(key)"
              class="w-7 h-7 rounded-full border-2 transition-all duration-200 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
              :class="accentColor === key ? 'border-brand-text scale-110' : 'border-transparent'"
              :style="{ backgroundColor: `rgb(${isDark ? color.dark.primary : color.light.primary})` }"
              :title="color.name"
              :aria-label="`Select ${color.name} accent color`"
              :aria-pressed="accentColor === key"
            ></button>
          </div>
        </div>
      </Transition>
    </div>

    <template v-if="isLoggedIn">
      <RouterLink
        to="/my-festivals"
        class="p-2 rounded-lg text-brand-muted hover:text-brand-accent hover:bg-brand-muted/10 transition-colors"
        :title="t('attendance.my_festivals')"
        :aria-label="t('attendance.my_festivals')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      </RouterLink>

      <RouterLink
        v-if="isAdmin"
        to="/admin"
        class="flex items-center space-x-1 text-gray-500 hover:text-brand-primary font-medium transition-colors"
        :title="t('nav.admin')"
        :aria-label="t('nav.admin')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </RouterLink>

      <RouterLink
        to="/profile"
        class="flex items-center space-x-1 text-gray-500 hover:text-brand-primary font-medium transition-colors"
        :aria-label="t('nav.profile')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      </RouterLink>
    </template>
    <template v-else>
      <RouterLink
        :to="loginRedirect"
        class="p-2 rounded-lg text-brand-muted hover:text-brand-accent hover:bg-brand-muted/10 transition-colors"
        :title="t('nav.login')"
        :aria-label="t('nav.login')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      </RouterLink>
    </template>
  </div>
</template>